import * as _ from 'lodash';
import { Observable, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { MediaMatcher } from '@angular/cdk/layout';
// Import external
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
// Import internal
import { Level } from '@data/models/role.model';
import { environment } from '@env/environment';

import { Cell } from '@data/models/cell.model';
import { CellService } from '@data/services/cell.service';
import { AvatarService } from '@app/services/avatar.service';
import { UserSession, UserSessionService } from '@app/services/user-session.service';
import { AuthService } from '@auth0/auth0-angular';

const SECURITY_GROUPS_EXTERNAL_LINK = 'https://myaccount.microsoft.com/groups/groups-i-belong-to';

@Component({
    selector: 'syn-layout',
    templateUrl: 'main-layout.component.html',
    styleUrls: ['main-layout.component.scss'],
})
export class MainLayoutComponent implements OnInit, OnDestroy {

    public isNotProduction = environment.environment !== 'prod';

    public docUrl = `${environment.url}/console`;

    public account: Partial<UserSession>;

    public defaultCell = new Cell({
        id: 'all',
        name: 'All',
        description: 'Toutes les offres',
        color: '#da001a'
    });

    public selectedCell: Cell;
    public cells: Cell[];

    public globalSearchForm: FormGroup;

    public canRead = {
        inventory: false,
        projects: false,
        companies: false,
        users: false,
        tickets: false,
        tribe: false,
        cell: false,
        app: false,
    };

    public version = environment.release;
    public oneViewUrl = environment.oneviewUrl;

    public mobileQuery: MediaQueryList;
    public tabletQuery: MediaQueryList;

    public readonly accountSecurityGroupsUrl = SECURITY_GROUPS_EXTERNAL_LINK;

    private cellSubscription: Subscription;
    private auth0Subscription: Subscription;

    constructor(
        private activatedRoute: ActivatedRoute,
        private formBuilder: FormBuilder,
        private router: Router,
        private route: ActivatedRoute,
        private avatarService: AvatarService,
        private cellService: CellService,
        private userSessionService: UserSessionService,
        private media: MediaMatcher,
        private authService: AuthService
    ) { }

    public get containerClass(): string {
        return this.hasNestedNav() ? 'main-container-padding-free' : 'main-container';
    }

    public get sidenavContentClass(): string {
        return this.hasNestedNav() ? 'nested-sidenav' : '';
    }

    public ngOnDestroy(): void {
        if (this.cellSubscription) {
            this.cellSubscription.unsubscribe();
        }
        if (this.auth0Subscription) {
            this.auth0Subscription.unsubscribe();
        }
    }

    public async switchCells(cell) {
        this.selectedCell = cell;
        if (cell.id === 'all') {
            localStorage.removeItem('currentCell');
        } else {
            localStorage.setItem('currentCell', cell.id);
        }

        // when we change cell we want to go to the listing view
        const destinationUrl = `/${this.activatedRoute.snapshot.firstChild.routeConfig.path}`;

        // force reload if we are going to the same page
        if (this.router.url === destinationUrl) {
            location.reload();
        }
        await this.router.navigate([destinationUrl]);
    }

    public async doSearch() {
        const term = this.globalSearchForm.value.term;
        if (term.length) {
            await this.router.navigate(['/search'], { queryParams: { term: encodeURIComponent(term) } });
        }
    }

    public logout(): void {
        this.authService.logout({
            logoutParams: {
                returnTo: `${window.location.origin}/login`
            }
        });
    }

    public ngOnInit(): void {
        this.globalSearchForm = this.formBuilder.group({
            term: ['', Validators.required],
        });

        this.auth0Subscription = this.authService.isAuthenticated$.subscribe((_isLogged) => {
            this.account = this.getUserSession();
            this.canRead = this.getCanRead();
        });

        this.cellSubscription = this.cellService
            .findCells()
            .pipe(
                catchError((err, caught: Observable<Cell[]>) => {
                    localStorage.removeItem('currentCell');
                    this.selectedCell = this.defaultCell;
                    return of([]);
                })
            )
            .subscribe(cells => {
                this.cells = _.concat(this.defaultCell, cells);
                _.map(this.cells, cellItem => {
                    cellItem.color = cellItem.color ? cellItem.color : this.avatarService.getColor(cellItem.name);
                    cellItem.avatarInitials = cellItem.id === 'all' ? 'All' : this.avatarService.getInitials(cellItem.name);
                });
                const currentCell = localStorage.getItem('currentCell') || 'all';
                this.selectedCell = _.find(this.cells, ['id', currentCell]);
            });
        this.mobileQuery = this.media.matchMedia('(max-width: 550px)');
        this.tabletQuery = this.media.matchMedia('(max-width: 770px)');
    }

    private getCanRead() {
        return {
            inventory: this.userSessionService.checkRight('inventory', Level.READ),
            projects: this.userSessionService.checkRight('project', Level.READ),
            companies: this.userSessionService.checkRight('company', Level.READ),
            users: this.userSessionService.checkRight('user', Level.READ),
            tickets: this.userSessionService.checkRight('ticket', Level.READ),
            tribe: this.userSessionService.checkRight('tribe', Level.READ),
            cell: this.userSessionService.checkRight('cell', Level.READ),
            app: this.userSessionService.checkRight('app', Level.READ),
            'business-project': this.userSessionService.checkRight('business-project', Level.READ),
        };
    }

    private getUserSession() {
        const userSession = this.userSessionService.getUserSession();
        return {
            id: userSession.id,
            email: userSession.email,
            name: userSession.name,
            firstName: userSession.firstName,
            lastName: userSession.lastName,
            avatar: userSession.avatar,
            resetPasswordUrl: userSession.resetPasswordUrl
        };
    }

    private hasNestedNav(): boolean {
        return checkHasNestedRoutes(this.route);
    }
}

const checkHasNestedRoutes = (route: ActivatedRoute): boolean => {
    if (route?.snapshot?.data && route.snapshot.data['hasNestedRoutes'] && route.snapshot.data['hasNestedRoutes'] === true) {
        return true;
    }
    if (route.children.length === 0) {
        return false;
    }
    return route.children.some(checkHasNestedRoutes);
};
